import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import {
  useCreateMilestoneTemplate, useDeleteMilestoneTemplate,
  useMilestoneTemplates, useUpdateMilestoneTemplate,
} from "@/api/hooks/milestoneTemplates";
import type { MilestoneTemplateRow } from "@/api/types";

const STUDY_KINDS = [
  { value: "master", label: "Cohort (master)" },
  { value: "pharma_child", label: "Sponsored study (pharma)" },
] as const;

/**
 * Milestone-template editor. Two stacked tables (one per study_kind)
 * with a top-bar create form scoped to the active kind. Each row is
 * editable (label, display_order, notes) via the inline dialog.
 */
export function MilestonesPage() {
  const query = useMilestoneTemplates();
  const rows = query.data ?? [];
  const masters = rows.filter((r) => r.study_kind === "master");
  const pharma = rows.filter((r) => r.study_kind === "pharma_child");

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Milestones
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Defines the pill bar above each study card. Slug must match the
        ParticipationTask <code>task_type</code> when status is sourced
        from gated tasks; otherwise the pill renders gray as a tracker.
      </Typography>
      {query.isLoading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {query.error && (
        <Alert severity="error">
          Could not load milestones: {(query.error as Error).message}
        </Alert>
      )}
      {!query.isLoading && (
        <Stack spacing={3}>
          <MilestoneTable
            studyKind="master"
            heading="Cohort milestones"
            rows={masters}
          />
          <MilestoneTable
            studyKind="pharma_child"
            heading="Sponsored-study milestones"
            rows={pharma}
          />
        </Stack>
      )}
    </Box>
  );
}

function MilestoneTable({
  studyKind, heading, rows,
}: { studyKind: "master" | "pharma_child"; heading: string; rows: MilestoneTemplateRow[] }) {
  const create = useCreateMilestoneTemplate();
  const remove = useDeleteMilestoneTemplate();
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [order, setOrder] = useState<number>(
    rows.length > 0 ? rows[rows.length - 1].display_order + 10 : 10,
  );
  const [editing, setEditing] = useState<MilestoneTemplateRow | null>(null);

  const onCreate = () => {
    if (!slug.trim() || !label.trim()) return;
    create.mutate(
      {
        study_kind: studyKind,
        slug: slug.trim(),
        label: label.trim(),
        display_order: order,
      },
      {
        onSuccess: () => {
          setSlug("");
          setLabel("");
          setOrder(order + 10);
        },
      },
    );
  };

  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, display: "block", mb: 1 }}
      >
        {heading} · {rows.length}
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "center" }, mb: 2, flexWrap: "wrap" }}
      >
        <TextField
          size="small" label="Slug" placeholder="feasibility"
          value={slug} onChange={(e) => setSlug(e.target.value)}
          sx={{ minWidth: 180 }}
        />
        <TextField
          size="small" label="Label" placeholder="Feasibility"
          value={label} onChange={(e) => setLabel(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small" label="Order" type="number"
          value={order}
          onChange={(e) => setOrder(Number(e.target.value) || 0)}
          sx={{ width: 110 }}
        />
        <Button
          variant="contained"
          disabled={create.isPending || !slug.trim() || !label.trim()}
          onClick={onCreate}
        >
          {create.isPending ? "Adding…" : "Add milestone"}
        </Button>
      </Stack>
      {create.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {create.error.message}
        </Alert>
      )}
      {rows.length === 0 ? (
        <Typography color="text.secondary">
          No milestones for this study kind yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Order</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right" sx={{ width: 110 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                  {r.display_order}
                </TableCell>
                <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                  {r.slug}
                </TableCell>
                <TableCell>{r.label}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {r.notes ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing(r)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (window.confirm(`Delete milestone "${r.label}"?`)) {
                            remove.mutate(r.id);
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing && (
        <EditMilestoneDialog
          key={editing.id}
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Box>
  );
}

function EditMilestoneDialog({
  row, onClose,
}: { row: MilestoneTemplateRow; onClose: () => void }) {
  const [label, setLabel] = useState(row.label);
  const [order, setOrder] = useState<number>(row.display_order);
  const [notes, setNotes] = useState(row.notes ?? "");
  const update = useUpdateMilestoneTemplate();

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit milestone · {row.slug}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select label="Study kind" value={row.study_kind} disabled
          >
            {STUDY_KINDS.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
          <TextField
            label="Display order" type="number"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value) || 0)}
          />
          <TextField
            label="Notes" multiline minRows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)}
          />
          {update.error && (
            <Alert severity="error">{update.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={update.isPending || !label.trim()}
          onClick={() =>
            update.mutate(
              {
                id: row.id, label: label.trim(),
                display_order: order, notes: notes.trim() || null,
              },
              { onSuccess: onClose },
            )
          }
        >
          {update.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
